import React, { useEffect, useMemo } from "react";
import { CheckCircle2, Circle, BookOpen, Highlighter } from "lucide-react";
import { Blocks } from "./Blocks";
import { useLocalStorage } from "@/lib/storage";

const HL_MODES = [
  { key: "on", label: "Vurgular açık" },
  { key: "focus", label: "Odak modu" },
  { key: "off", label: "Vurgusuz" },
];

export default function NotesView({ course, read, setRead, unitId, setUnitId }) {
  const units = course.notes;
  const unit = useMemo(() => units.find((u) => u.id === unitId) || units[0], [units, unitId]);
  const key = (u) => `${course.key}-${u.id}`;
  const readCount = units.filter((u) => read[key(u)]).length;
  const [hlMode, setHlMode] = useLocalStorage("hlmode", "on");

  // Mobilde içindekiler yatay şerit olur; aktif üniteyi görünür alana getir
  useEffect(() => {
    const el = document.querySelector(".toc-item.active");
    if (el && window.matchMedia("(max-width: 1024px)").matches) el.scrollIntoView({ block: "nearest", inline: "center" });
  }, [unit.id]);

  const go = (id) => {
    setUnitId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="notes-layout">
      <aside className="notes-toc" data-testid="notes-toc">
        <div className="toc-head">
          <BookOpen size={15} /> İçindekiler
          <span className="toc-progress">{readCount}/{units.length} okundu</span>
        </div>
        <div className="toc-bar"><div style={{ width: `${(readCount / units.length) * 100}%` }} /></div>
        {units.map((u) => (
          <button
            key={u.id}
            className={`toc-item ${u.id === unit.id ? "active" : ""}`}
            onClick={() => go(u.id)}
            data-testid={`notes-unit-${u.id}`}
          >
            <span className="toc-no">{u.no}</span>
            <span className="toc-title">{u.title}</span>
            {read[key(u)] ? <CheckCircle2 size={15} className="toc-check" /> : <Circle size={15} className="toc-dot" />}
          </button>
        ))}
        <div className="toc-sections">
          {unit.sections.map((s, i) => (
            <a key={i} href={`#sec-${unit.id}-${i}`} className="toc-sec">{s.h}</a>
          ))}
        </div>
      </aside>

      <article className={`notes-body ${hlMode === "off" ? "hl-off" : hlMode === "focus" ? "hl-focus" : ""}`} data-testid="notes-body" data-hl-mode={hlMode}>
        <div className="notes-tools" data-testid="notes-tools">
          <div className="chips">
            <span className="eyebrow"><Highlighter size={13} /> Vurgu</span>
            {HL_MODES.map((m) => (
              <button key={m.key} className={`chip ${hlMode === m.key ? "active" : ""}`} onClick={() => setHlMode(m.key)} data-testid={`hl-mode-${m.key}`}>{m.label}</button>
            ))}
          </div>
          <div className="hl-legend" data-testid="hl-legend">
            <span><mark className="hl">fosforlu</mark> = sınav için kritik bilgi / sayı</span>
            <span><u className="ul">altı çizili</u> = anahtar tanım / terim</span>
          </div>
        </div>
        <div className="unit-hero">
          <span className="eyebrow">{course.code} · {unit.no}</span>
          <h1 className="unit-title">{unit.title}</h1>
          <p className="unit-meta">{unit.sections.length} bölüm · Tam sunum ders notu · {readCount}/{units.length} ünite okundu</p>
        </div>
        {unit.sections.map((s, i) => (
          <section key={i} id={`sec-${unit.id}-${i}`} className="nb-section">
            <h2 className="nb-h2">{s.h}</h2>
            <Blocks blocks={s.blocks} />
          </section>
        ))}
        <div className="unit-footer">
          <button
            className={`btn ${read[key(unit)] ? "btn-ghost" : "btn-accent"}`}
            onClick={() => setRead({ ...read, [key(unit)]: !read[key(unit)] })}
            data-testid="mark-read-btn"
          >
            <CheckCircle2 size={16} /> {read[key(unit)] ? "Okundu olarak işaretlendi" : "Bu üniteyi okudum"}
          </button>
          {units.findIndex((u) => u.id === unit.id) < units.length - 1 && (
            <button
              className="btn btn-ghost"
              onClick={() => go(units[units.findIndex((u) => u.id === unit.id) + 1].id)}
              data-testid="next-unit-btn"
            >
              Sonraki ünite →
            </button>
          )}
        </div>
      </article>
    </div>
  );
}
